/** @author Rajkumar Rathod */
export const SDK_VERSION = __SDK_VERSION__;

/** Origin serving the embed pages (`/embed/find-doctor`, `/embed/register`). */
export const DEFAULT_HOST = __DEFAULT_HOST__;

/**
 * Base URL of the EHR REST API the embed calls for providers, slots, patients and
 * appointments. Separate from DEFAULT_HOST because the page serving the embed UI and
 * the API answering its fetches are usually different deployments.
 *
 * Forwarded to the embed in `host:init` rather than the URL, so a client can point one
 * CDN build at a different backend (staging, a regional cluster) without a rebuild.
 */
export const DEFAULT_API_BASE = __DEFAULT_API_BASE__;

/** Bumped only on breaking postMessage changes. The embed checks it and can refuse. */
export const PROTOCOL_VERSION = 1;

export const GLOBAL_NAME = 'EhrWidget';

/** Message-type prefix. Namespaced so we never react to unrelated postMessage traffic. */
export const MSG_NS = 'ehrw';

/** Pseudo-flow: opens the anchored menu instead of an embed session. */
export const REQUEST_APPOINTMENT_FLOW = 'request-appointment';

export const DEFAULT_FLOW_PATHS: Record<string, string> = {
  'find-doctor': '/embed/find-doctor',
  register: '/embed/register',
  login: '/embed/login',
};

/**
 * Selectors bound out of the box.
 *
 * `#widget-ech-*` are the ids from the original spec; the `#widget-ehr-*` spellings are
 * accepted too so a transposed "ehr"/"ech" does not silently fail to bind.
 *
 * The `[data-ehr-widget="..."]` form is the one to prefer in real pages: an id must be
 * unique per document, so ids cannot express "three Book Appointment buttons on one page".
 */
export const DEFAULT_TRIGGERS: Record<string, string[]> = {
  'find-doctor': [
    '#widget-ech-find-doctor',
    '#widget-ehr-find-doctor',
    '[data-ehr-widget="find-doctor"]',
    '[data-ehr-widget="book-appointment"]',
  ],
  register: [
    '#widget-ech-register',
    '#widget-ehr-register',
    '[data-ehr-widget="register"]',
    // The "New Patient" arm of a Request Appointment menu.
    '[data-ehr-widget="new-patient"]',
  ],
  /**
   * Opens a small menu under the button rather than the overlay, letting the
   * patient pick New or Existing before anything is loaded cross-origin.
   */
  'request-appointment': [
    '#widget-ech-request-appointment',
    '#widget-ehr-request-appointment',
    '[data-ehr-widget="request-appointment"]',
  ],
  
  /**
   * Returning patients. Paired with `register` behind a "Request Appointment"
   * menu on the client's site: New Patient opens the registration form,
   * Existing Patient opens this.
   */
  login: [
    '#widget-ech-login',
    '#widget-ehr-login',
    '[data-ehr-widget="login"]',
    '[data-ehr-widget="existing-patient"]',
  ],
};

export const DEFAULT_READY_TIMEOUT_MS = 15_000;

/**
 * `allow-same-origin` is scoped to the *embed's* origin, not the host's, so the EHR app
 * keeps its own cookies/storage while staying walled off from the host page.
 * `allow-popups-to-escape-sandbox` keeps third-party auth/payment redirects working.
 *
 * Deliberately **not** included: `allow-top-navigation-by-user-activation`. It would let
 * the embed navigate the client's top-level page away, which is a redirect the client
 * cannot see coming and cannot undo. Third-party auth and payment redirects run in the
 * popup instead. A client whose flow genuinely needs it can opt in per integration with
 * `sandbox: DEFAULT_SANDBOX + ' allow-top-navigation-by-user-activation'`.
 */
export const DEFAULT_SANDBOX = [
  'allow-scripts',
  'allow-same-origin',
  'allow-forms',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
].join(' ');

/**
 * Permissions delegated to the frame. Least privilege: only what a booking/registration
 * flow actually uses. `geolocation`, `camera` and `microphone` are **not** delegated by
 * default — a telehealth or ID-scan flow that needs them opts in per integration with
 * `allow: 'payment; clipboard-write; camera; microphone'`.
 */
export const DEFAULT_IFRAME_ALLOW = 'payment; clipboard-write';

/**
 * Query keys the SDK owns. `open({ params })` cannot set them: `origin` is what the EHR
 * matches against the tenant allowlist and `sid` binds the handshake, so a host that
 * forwards user-controlled values into `params` must not be able to forge either.
 */
export const RESERVED_QUERY_PARAMS: ReadonlySet<string> = new Set([
  'clientId',
  'flow',
  'origin',
  'sid',
  'sdk',
  'pv',
  'theme',
  'locale',
  'embed',
]);

export const Z_INDEX = 2_147_483_000;
